import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { UpdateAPInvoiceInput, UpdateAPInvoiceResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * Hook for updating AP invoice fields.
 * Handles the API call, success/error states, and cache invalidation.
 */
export function useUpdateAPInvoice() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<UpdateAPInvoiceResponse, Error, UpdateAPInvoiceInput>({
    mutationFn: async (input) => {
      return await rpcClient.accountsPayable.invoices.update(input);
    },
    onSuccess: (data, variables) => {
      showToast({
        title: 'Success',
        message: 'Invoice updated successfully',
        color: 'success',
        icon: 'check',
      });

      // Invalidate invoice queries to refresh the data
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsPayable.invoices.get.queryKey({
          input: { id: variables.id },
        }),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.accountsPayable.invoices.list.queryKey({
          input: {},
        }),
      });
    },
    onError: (error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update invoice',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
